/**
 * Idealista Frontend Service
 * Manages token storage and API communication
 */

const TOKEN_KEY = 'idealista_token';
const TOKEN_EXPIRY_KEY = 'idealista_token_expiry';

export interface IdealistaSearchParams {
  city: string;
  contractType?: 'rent' | 'sale';
  minPrice?: number;
  maxPrice?: number;
  minSurface?: number;
  maxSurface?: number;
  minRooms?: number;
  maxRooms?: number;
  page?: number;
  limit?: number;
}

// ============================================================================
// Token Management
// ============================================================================

/**
 * Save the Idealista token to localStorage
 * Token expires in 1 hour by default
 */
export function saveToken(token: string, expiresInHours: number = 1): void {
  const expiryTime = Date.now() + (expiresInHours * 60 * 60 * 1000);
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiryTime));
}

/**
 * Get the stored token (if valid)
 */
export function getToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!token || !expiry) {
    return null;
  }

  // Check if expired
  if (Date.now() > Number(expiry)) {
    clearToken();
    return null;
  }

  return token;
}

/**
 * Clear the stored token
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
}

/**
 * Check if we have a valid token
 */
export function hasValidToken(): boolean {
  return getToken() !== null;
}

/**
 * Get remaining token validity time in minutes
 */
export function getTokenRemainingMinutes(): number {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return 0;

  const remaining = Number(expiry) - Date.now();
  return Math.max(0, Math.floor(remaining / 60000));
}

// ============================================================================
// API Methods
// ============================================================================

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3020/api';

/**
 * Search properties on Idealista using the stored token
 */
export async function searchProperties(params: IdealistaSearchParams) {
  const token = getToken();
  if (!token) {
    throw new Error('No Idealista token available. Please provide your token.');
  }

  const response = await fetch(`${API_BASE}/listings/idealista/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token,
      ...params,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));

    // If unauthorized, clear the token
    if (response.status === 401) {
      clearToken();
    }

    throw new Error(error.message || `Search failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Validate a token by making a test request
 */
export async function validateToken(token: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/listings/idealista/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        city: 'milano',
        contractType: 'rent',
        limit: 1,
      }),
    });

    return response.ok;
  } catch {
    return false;
  }
}
